import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Alert, Spinner, Image } from 'react-bootstrap';
import { storage } from '../../firebaseConfig'; // Import Firebase config
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import MainContainer from '../common/MainContainer';
import { useNavigate, useParams } from 'react-router-dom';
import { Category } from '../../model/CategoryModel';
import { CategoryGateway } from '../../gateways/CategoryGateway';
import ImageSelectorModal from '../common/ImageSelectorModal';
import { Utils } from '../../common/Utils';
import ImageCropModal from '../common/ImageCropModal';

interface CategoryFormProps {
    categoryId?: string;
    existingData?: Category;
}

const ManageCategory: React.FC<CategoryFormProps> = () => {
    const { categoryId } = useParams(); // Retrieve category ID from URL
    const [category, setCategory] = useState<Category>({ name: '', description: '' });
    const [image, setImage] = useState<Blob | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [saveText, setSaveText] = useState<string>("");
    const [showCropModal, setShowCropModal] = useState(false);
    const [croppedImageBlob, setCroppedImageBlob] = useState<Blob | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [croppedImagePreviewUrl, setCroppedImagePreviewUrl] = useState<string | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchCategory = async () => {
            if (!categoryId) return;
            setLoading(true);
            try {
                const category = await CategoryGateway.GetCategory(categoryId);
                if (category) {
                    setCategory(category);
                } else {
                    setError('Category not found.');
                }
            } catch (err) {
                setError('Error fetching category.');
            }
            setLoading(false);
        };
        fetchCategory();
    }, [categoryId]);

    useEffect(() => {
        if (!image && fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    }, [image]);


    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setCategory({ ...category, [e.target.name]: e.target.value });
    };

    const deleteImage = async () => {
        if (!category.imageUrl) return;
        try {
            setLoading(true);
            const imageRef = ref(storage, category.imageUrl);
            await deleteObject(imageRef);
            await CategoryGateway.UpdateCategory(categoryId!, { ...category, imageUrl: '' });
            setCategory((prev) => ({ ...prev, imageUrl: '' }));
        } catch {
            setError('Error deleting image.');
        }
        finally {
            setLoading(false);
        }
    };


    const uploadImageAndSaveCategory = async () => {
        setLoading(true);

        if (!croppedImageBlob) {
            return categoryId ? updateCategory(category.imageUrl) : addCategory(category.imageUrl);
        }

        const storageRef = ref(storage, `categories/${Date.now()}_${category.name}.jpg`);
        const uploadTask = uploadBytesResumable(storageRef, croppedImageBlob);

        uploadTask.on(
            'state_changed',
            null,
            (error) => {
                setLoading(false);
                setError('Error uploading image.');
                console.error("Error saving the image", error);
            },
            async () => {
                setLoading(true);
                const imageUrl = await getDownloadURL(uploadTask.snapshot.ref);
                setCategory((prev) => ({ ...prev, imageUrl }));
                categoryId ? updateCategory(imageUrl) : addCategory(imageUrl);
            }
        );
    };

    const addCategory = async (imageUrl?: string) => {
        try {
            const id = await CategoryGateway.AddCategory({ ...category, imageUrl: imageUrl ?? '' });
            setCroppedImagePreviewUrl(null);
            setCroppedImageBlob(null);
            setSaveText('Saved Successfully');
            navigate(Utils.GetUrl(`/admin/category/${id}`));
            // Wait 2 seconds before resetting
            setTimeout(() => {
                setSaveText('');
                setLoading(false);
            }, 2000);
        } catch (err: any) {
            setError('Error saving category.');
            console.error("Error saving:", err);
        }
    };

    const updateCategory = async (imageUrl?: string) => {
        try {
            await CategoryGateway.UpdateCategory(categoryId!, { ...category, imageUrl: imageUrl ?? '' });
            setCroppedImagePreviewUrl(null);
            setCroppedImageBlob(null);
            setSaveText('Saved Successfully');
            // Wait 2 seconds before resetting
            setTimeout(() => {
                setSaveText('');
                setLoading(false);
            }, 2000);
        } catch (err: any) {
            setError('Error updating category.');
            console.error("Error updating:", err);
        }
    };

    return (
        <MainContainer>
            <h2 className="mb-4">{categoryId ? 'Update Category' : 'Add Category'}</h2>
            <Form onSubmit={(e) => { e.preventDefault(); uploadImageAndSaveCategory(); }} className="p-3">
                {loading && <Spinner animation="border" />}
                {error && <Alert variant="danger">{error}</Alert>}
                <Form.Group controlId="categoryName" className="mb-3">
                    <Form.Label>Category Name</Form.Label>
                    <Form.Control type="text" name="name" value={category.name} onChange={handleChange} required />
                </Form.Group>

                <Form.Group controlId="categoryDesc" className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control as="textarea" name="description" value={category.description} onChange={handleChange} />
                </Form.Group>

                {!category.imageUrl && (<>
                    <Form.Group controlId="categoryImage" className="mb-3">
                        <Form.Label>Upload Image</Form.Label>
                        <Form.Control
                            type="file"
                            accept="image/*"
                            ref={fileInputRef}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                    setImage(file);
                                    setShowCropModal(true);
                                }
                            }}
                        />
                    </Form.Group>
                    <Form.Group controlId="categoryImageSearch" className="mb-3">
                        <Form.Label>Search Image</Form.Label>
                        <div className="d-flex gap-2">
                            <ImageSelectorModal
                                entityName={category.name}
                                onImageSelected={(blob) => {
                                    setImage(blob);
                                    setShowCropModal(true);

                                }}
                            />
                        </div>
                    </Form.Group></>
                )}

                {category.imageUrl && (
                    <div className='mb-3'>
                        <Image src={category.imageUrl} thumbnail width={150} />
                        <Button variant="danger" onClick={deleteImage} className="mt-2">{loading ? <Spinner animation="border" size="sm" /> : "Delete Image"}</Button>
                    </div>
                )}

                {croppedImagePreviewUrl && (
                    <div className='mb-3'>
                        <Image src={croppedImagePreviewUrl} thumbnail width={150} />
                        <div className="text-muted" style={{ fontStyle: "italic" }}>Cropped preview (not yet saved)</div>
                    </div>
                )}


                <Button variant="primary" type="submit">
                    {loading ? <Spinner animation="border" size="sm" /> : "Save Category"}</Button>
                <Button variant='secondary' type='button' className='mx-2' onClick={() => { navigate(Utils.GetUrl("/admin/categories")) }}>
                    Back
                </Button>
                {saveText && <Alert variant="success" className="mb-0 mt-1 py-1 px-2">
                    {saveText}
                </Alert>}
            </Form>

            {showCropModal && image && (
                <ImageCropModal
                    show={showCropModal}
                    file={image}
                    onClose={() => {
                        setImage(null);
                        setShowCropModal(false);
                    }}
                    onUploadComplete={(blob) => {
                        setCroppedImageBlob(blob);
                        setImage(null);
                        const previewUrl = URL.createObjectURL(blob);
                        setCroppedImagePreviewUrl(previewUrl);
                        setShowCropModal(false);
                    }}
                />
            )}

        </MainContainer>
    );
};

export default ManageCategory;